import { z } from 'zod';

/** Stable machine-readable error codes. Add new codes here; never reuse a code for a different meaning. */
export const ERROR_CODES = [
  'bad_request',
  'validation_failed',
  'unauthorized',
  'session_expired',
  'forbidden',
  'csrf_failed',
  'insufficient_scope',
  'approval_required',
  'not_found',
  'provider_not_found',
  'provider_disabled',
  'oauth_state_mismatch',
  'oauth_failed',
  'conflict',
  'rate_limited',
  'guardrail_daily_cap',
  'guardrail_org_not_allowed',
  'account_suspended',
  'internal_error',
  'response_validation_failed',
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export const ErrorBodySchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ErrorBody = z.infer<typeof ErrorBodySchema>;

export class AppError extends Error {
  readonly code: ErrorCode | string;
  readonly status: number;
  readonly details: unknown;

  constructor(code: ErrorCode | string, status: number, message: string, details?: unknown) {
    super(message);
    if (!Number.isInteger(status) || status < 400 || status > 599) {
      throw new RangeError(`AppError status must be 4xx/5xx, got ${status}`);
    }
    this.name = 'AppError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export function toErrorBody(e: AppError): ErrorBody {
  return {
    error: {
      code: e.code,
      message: e.message,
      ...(e.details === undefined ? {} : { details: e.details }),
    },
  };
}

export const errors = {
  badRequest: (m: string, d?: unknown) => new AppError('bad_request', 400, m, d),
  validation: (m: string, d?: unknown) => new AppError('validation_failed', 400, m, d),
  unauthorized: (m: string, d?: unknown) => new AppError('unauthorized', 401, m, d),
  sessionExpired: (m = 'Session expired; sign in again') => new AppError('session_expired', 401, m),
  forbidden: (m: string, d?: unknown) => new AppError('forbidden', 403, m, d),
  csrf: (m: string) => new AppError('csrf_failed', 403, m),
  insufficientScope: (scope: string) =>
    new AppError('insufficient_scope', 403, `Missing required scope: ${scope}`, {
      required: scope,
    }),
  approvalRequired: (approvalId: string, m = 'Human approval required') =>
    new AppError('approval_required', 403, m, { approvalId }),
  notFound: (m: string, d?: unknown) => new AppError('not_found', 404, m, d),
  conflict: (m: string, d?: unknown) => new AppError('conflict', 409, m, d),
  rateLimited: (m: string, retryAfterSeconds: number) =>
    new AppError('rate_limited', 429, m, { retryAfterSeconds }),
  /** Agent token hit its human-set daily action cap (UTC day). */
  guardrailDailyCap: (cap: number, retryAfterSeconds: number) =>
    new AppError(
      'guardrail_daily_cap',
      429,
      `This agent token has used its daily action cap of ${cap} outward actions for today (UTC). Retry after the UTC day rolls over, or ask the token owner to raise dailyActionCap by rotating the token.`,
      { dailyActionCap: cap, retryAfterSeconds },
    ),
  /** Agent token acted on an org outside its human-set allow-list. */
  guardrailOrgNotAllowed: (orgId: string) =>
    new AppError(
      'guardrail_org_not_allowed',
      403,
      `This agent token is not allowed to act on organisation ${orgId}. Ask the token owner to add it to allowedOrgIds by rotating the token.`,
      { orgId },
    ),
  internal: (m: string, d?: unknown) => new AppError('internal_error', 500, m, d),
  /** Account is suspended (admin moderation action); no session/agent-token action may proceed. */
  accountSuspended: (reason: string) =>
    new AppError('account_suspended', 403, `Account suspended: ${reason}`, { reason }),
};
